import React, {useState, useEffect, useMemo} from 'react'
import BreadCrumb from '../../../Common/BreadCrumb'
import {Alert, Button, Card, Col, Container, Form, Row} from 'react-bootstrap'
import {useTranslation} from "react-i18next";
import { useNavigate } from 'react-router-dom';
import ConditionBuilder from "../../Components/ConditionBuilder";
import {dashboardAPI} from "../../../services/api";
import {mockScenarios} from "../../../data/scenarios";
import Select from "react-select";

const AddRule = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();

    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    const [scenarios, setScenarios] = useState<Scenario[]>(mockScenarios);
    const [formState, setFormState] = useState({
        name: "",
        description: "",
        scenarioId: "",
        conditionGroup: { join: "and", conditions: [] },
    });

    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch scenarios for scenario selection
    useEffect(() => {
        const fetchScenarios = async () => {
            if (isDemoUser) {
                setScenarios(mockScenarios);
                return;
            }
            try {
                const response: any = await dashboardAPI.getScenarios();
                const scenariosData = Array.isArray(response) ? response : (response?.data || []);
                setScenarios(scenariosData.map((s: any) => ({
                    id: s.id || '',
                    name: s.name || '',
                    active: s.active !== false,
                    createdOn: s.createdOn || '',
                    description: s.description || '',
                    conditions: []
                })));
            } catch (error) {
                console.error('Failed to fetch scenarios:', error);
            }
        };
        fetchScenarios();
    }, [isDemoUser]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormState({ ...formState, [name]: value });
    };
    const validate = () => {
        if (!formState.name.trim()) return t("Validation.rule_name_required");
        if (!formState.scenarioId) return t("Validation.scenario_required");
        if (formState.conditionGroup.conditions.length === 0) return t("Validation.at_least_one_condition");
        return null;
    };

    const handleConditionChange = (updatedGroup: any) => {
        setFormState({ ...formState, conditionGroup: updatedGroup });
    };

    const handleCancel = () => {
        navigate("/rules");
    };

    const handleSubmit = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setError(null);
        setIsSubmitting(true);

        try {
            if (isDemoUser) {
                // For demo, just navigate back
                console.log("New rule (demo):", formState);
                navigate("/rules");
            } else {
                // Create rule on backend
                console.log("Creating rule on backend:", formState);
                await dashboardAPI.createRule({
                    name: formState.name,
                    description: formState.description,
                    scenarioId: formState.scenarioId,
                    active: true,
                    conditionGroup: formState.conditionGroup
                });
                console.log("✅ Rule created successfully");
                navigate("/rules");
            }
        } catch (error: any) {
            console.error("❌ Failed to create rule:", error);
            setError(error?.response?.data?.message || error?.message || "Failed to create rule");
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Dashboard.header'), path: "/"},
                            {label: t('RuleList.page_title'), path: "/rules"},
                            {label: t('RuleList.add_rule'), path: "/rule-add"}
                        ]}
                    />
                    <Row className="my-4">
                        <Col>
                            <h3>{t("RuleList.add_rule")}</h3>
                        </Col>
                    </Row>
                    <Card>
                        <Card.Body>
                            <Form>
                                {error && <Alert variant="danger">{error}</Alert>}
                                <Form.Group className="mb-3">
                                    <Form.Label>{t("RuleList.scenario_name")}</Form.Label>
                                    <Select
                                        options={scenarios.map(s => ({ value: s.id, label: s.name }))}
                                        value={scenarios.find(s => s.id === formState.scenarioId) 
                                            ? { value: formState.scenarioId, label: scenarios.find(s => s.id === formState.scenarioId)?.name || '' }
                                            : null}
                                        onChange={(option: any) => {
                                            setFormState({ ...formState, scenarioId: option?.value || '' });
                                        }}
                                        placeholder={t("RuleList.select_scenario")}
                                        isSearchable={true}
                                        noOptionsMessage={() => t("RuleList.no_options") || "Seçenek yok"}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>{t("RuleList.rule_name")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formState.name}
                                        onChange={handleChange}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>{t("RuleList.rule_description")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="description"
                                        value={formState.description}
                                        onChange={handleChange}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>{t("RuleList.conditions")}</Form.Label>
                                    <ConditionBuilder
                                        group={formState.conditionGroup}
                                        onChange={handleConditionChange}
                                        t={t}
                                    />
                                </Form.Group>

                                <div className="d-flex justify-content-end gap-2">
                                    <Button variant="dark" onClick={handleCancel} disabled={isSubmitting}>{t("RuleList.cancel")}</Button>
                                    <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                                        {isSubmitting ? t("RuleList.saving") || "Saving..." : t("RuleList.save")}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default AddRule
